// /bestsellers — boot, and the two actions the page dispatches.
//
// Mounted from `main.ts` like every other feature module. Everything is a no-op unless the
// page actually has the tab bar, so the 25 other pages pay nothing for it.
import { registerActions } from "../../actionRegistry";
import { getDomElement } from "../../utils/dom";
import { initBestsellerUploads } from "./UploadStrip";
import { clearUploads } from "./uploads";
import { currentTab, loadCurrentTab, tabHref, type BestsellerTab } from "./tabs";

/** True when the bestsellers page is the one on screen. */
function onBestsellers(): boolean {
  return getDomElement("bestsellers-tabs") !== null;
}

/**
 * Switch tab.
 *
 * `pushState`, not a navigation: the grid, the filters sidebar and the thumb-size toggle are
 * all already on the page and reloading would throw away an open filter panel for nothing.
 * The URL still changes, so a tab can be linked and reloaded — the SSR page reads the same
 * segment on a cold load.
 */
function bestsellerstab(tab: string): void {
  const next = tab as BestsellerTab;
  const href = tabHref(next);
  if (location.pathname !== href) window.history.pushState(null, "", href);
  paintTabs();
  loadCurrentTab();
}

/** Re-rank tab 1 when the period changes. */
function bestsellersperiod(): void {
  loadCurrentTab();
}

/** Re-rank Global top sellers when the region or the period changes. */
function bestsellersglobal(): void {
  loadCurrentTab();
}

/**
 * Mark the active tab, and show only the controls that tab owns.
 *
 * In the DOM rather than in a component: the tab bar and the period dropdown are
 * server-rendered, so there is no Preact root to re-render — and adding one would mean the
 * grid's container became a component's child, which is exactly the coupling the `lm_*`
 * machinery avoids.
 */
function paintTabs(): void {
  const active = currentTab();
  for (const el of document.querySelectorAll<HTMLElement>("[data-bestsellers-tab]")) {
    const isActive = el.dataset.bestsellersTab === active;
    el.classList.toggle("modal_tabs_tab_active", isActive);
  }
  const period = getDomElement("bestsellers-period-wrap");
  // "block", matching the SSR — see the note on the element in `views/pages/Bestsellers.tsx`
  // for why an inline-block cannot right-align its own contents.
  if (period) period.style.display = active === "mine" ? "block" : "none";
  const global = getDomElement("bestsellers-global-wrap");
  if (global) global.style.display = active === "global" ? "block" : "none";
  const uploads = getDomElement(`bestseller-uploads-root`);
  if (uploads) uploads.style.display = active === "upload" ? "block" : "none";
}

export function initBestsellers(): void {
  if (!onBestsellers()) return;
  initBestsellerUploads();
  paintTabs();

  // Back/forward between tabs. The tab lives in the URL, so the browser's own buttons have
  // to work — without this they change the address bar and leave the grid on the old tab.
  window.addEventListener("popstate", () => {
    if (!onBestsellers()) return;
    paintTabs();
    loadCurrentTab();
  });

  // The uploads are per visit, not per session: a reseller returning tomorrow should not
  // find yesterday's comparison still loaded with no indication of where it came from.
  clearUploads();

  loadCurrentTab();
}

registerActions({ bestsellerstab, bestsellersperiod, bestsellersglobal });
